import React, { useEffect, useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";

import {
  BillingProvider,
  CreateOrganisationResponse,
  SubscriptionState,
  SubscriptionTerm,
  createOrganisation,
} from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";
import GenericDialog from "components/shared/generic-dialog";
import { DEFAULT_REGION_CODE } from "constants/index";
import { GrpcError } from "context/snackbar-context";
import { getScUserClaims } from "helpers/get-sc-user-name";

interface CreateOrgDialogProps {
  open: boolean;
  onClose: () => void;
}

interface FormState {
  orgName: string;
  password: string;
  billingProvider: BillingProvider;
  subscriptionState: SubscriptionState;
  subscriptionTerm: SubscriptionTerm;
  couponCode: string;
}

const CreateOrgDialog: React.FC<CreateOrgDialogProps> = ({ open, onClose }) => {
  const [formState, setFormState] = useState<FormState>({
    orgName: "",
    password: "",
    billingProvider: BillingProvider.ZUORA,
    subscriptionState: SubscriptionState.FREE,
    subscriptionTerm: SubscriptionTerm.MONTHLY,
    couponCode: "INTERNAL",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<CreateOrganisationResponse | null>(null);
  const [error, setError] = useState<GrpcError | undefined>(undefined);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  useEffect(() => {
    getScUserClaims().then((claims) => {
      setFirstName(claims.givenName);
      setLastName(claims.familyName);
    });
  }, []);

  const isFreeState = formState.subscriptionState === SubscriptionState.FREE;
  const requiresCoupon =
    formState.subscriptionState === SubscriptionState.COUPLED_TRIAL ||
    formState.subscriptionState === SubscriptionState.ACTIVE;

  const handleSubmit = async () => {
    setError(undefined);
    setIsProcessing(true);
    try {
      const headers = callOptionsHeaders(DEFAULT_REGION_CODE);
      const resp = await createOrganisation(
        {
          orgName:
            formState.orgName || (firstName ? `${firstName}'s Team` : ""),
          password: formState.password,
          firstName,
          lastName,
          billingProvider: formState.billingProvider,
          subscriptionState: formState.subscriptionState,
          subscriptionTerm: isFreeState
            ? SubscriptionTerm.UNSPECIFIED
            : formState.subscriptionTerm,
          couponCode: requiresCoupon ? formState.couponCode : "",
        },
        headers,
      );
      setResult(resp);
    } catch (e) {
      setError(e as GrpcError);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setError(undefined);
    setFormState({
      orgName: "",
      password: "",
      billingProvider: BillingProvider.ZUORA,
      subscriptionState: SubscriptionState.FREE,
      subscriptionTerm: SubscriptionTerm.MONTHLY,
      couponCode: "INTERNAL",
    });
    onClose();
  };

  const isFormValid = () => {
    // Org name is optional — defaults to "{firstname}'s Team" on the backend.
    if (requiresCoupon && !formState.couponCode.trim()) return false;
    return true;
  };

  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = (value: string, field: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const CopyButton = ({ value, field }: { value: string; field: string }) => (
    <Tooltip title={copiedField === field ? "Copied!" : "Copy"}>
      <IconButton
        size="small"
        onClick={() => handleCopy(value, field)}
        sx={{ ml: 0.5 }}
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const renderResult = () => {
    if (!result) return null;
    const orgId = result.orgId;
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          <strong>Organisation Created Successfully</strong>
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body2">
            <strong>Org ID:</strong> {result.orgId}
          </Typography>
          <CopyButton value={result.orgId} field="orgId" />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body2">
            <strong>User ID:</strong> {result.userId}
          </Typography>
          <CopyButton value={result.userId} field="userId" />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body2">
            <strong>Email:</strong> {result.email}
          </Typography>
          <CopyButton value={result.email} field="email" />
        </Box>
        <Box sx={{ mt: 1 }}>
          <Link to={`/organisation/${DEFAULT_REGION_CODE}/${orgId}`}>
            View Org in Periscope
          </Link>
        </Box>
      </Alert>
    );
  };

  return (
    <GenericDialog
      title="Create Organisation"
      open={open}
      handleDialogClose={handleClose}
      hasXCloseBtn={true}
      hasAckBtn={!result}
      acknowledgeBtnLabel="Create Organisation"
      disabled={isProcessing || !isFormValid()}
      isLoading={isProcessing}
      handleAckBtnClick={handleSubmit}
      width="md"
    >
      <Box sx={{ width: "100%", p: 1 }}>
        {result ? (
          renderResult()
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              This utility creates a test organisation using your email address.
              It is only available in dev and sandpit environments and allows
              you to configure the billing provider and subscription state for
              testing purposes.
            </Alert>

            {isProcessing && (
              <Grid container justifyContent="center" sx={{ mb: 2 }}>
                <CircularProgress size={30} />
              </Grid>
            )}

            <TextField
              fullWidth
              margin="dense"
              label="Org Name"
              placeholder={firstName ? `${firstName}'s Team` : ""}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              value={formState.orgName}
              onChange={(e) =>
                setFormState({ ...formState, orgName: e.target.value })
              }
              disabled={isProcessing}
              inputProps={{ "data-testid": "org-name-input" }}
            />

            <TextField
              fullWidth
              margin="dense"
              label="Password (optional — random if empty)"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              value={formState.password}
              onChange={(e) =>
                setFormState({ ...formState, password: e.target.value })
              }
              disabled={isProcessing}
              inputProps={{ "data-testid": "password-input" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Divider sx={{ my: 2 }} />

            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">Billing Provider</FormLabel>
              <RadioGroup
                row
                value={formState.billingProvider}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    billingProvider: Number(e.target.value) as BillingProvider,
                  })
                }
              >
                <FormControlLabel
                  value={BillingProvider.CHARGIFY}
                  control={<Radio />}
                  label="Chargify"
                  disabled={isProcessing}
                />
                <FormControlLabel
                  value={BillingProvider.ZUORA}
                  control={<Radio />}
                  label="Zuora"
                  disabled={isProcessing}
                />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">Subscription State</FormLabel>
              <RadioGroup
                row
                value={formState.subscriptionState}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    subscriptionState: Number(
                      e.target.value,
                    ) as SubscriptionState,
                  })
                }
              >
                <FormControlLabel
                  value={SubscriptionState.FREE}
                  control={<Radio />}
                  label="Free"
                  disabled={isProcessing}
                />
                <FormControlLabel
                  value={SubscriptionState.DECOUPLED_TRIAL}
                  control={<Radio />}
                  label="Decoupled Trial"
                  disabled={isProcessing}
                />
                <FormControlLabel
                  value={SubscriptionState.COUPLED_TRIAL}
                  control={<Radio />}
                  label="Coupled Trial"
                  disabled={isProcessing}
                />
                <FormControlLabel
                  value={SubscriptionState.ACTIVE}
                  control={<Radio />}
                  label="Active Subscription"
                  disabled={isProcessing}
                />
              </RadioGroup>
            </FormControl>

            {!isFreeState && (
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <FormLabel component="legend">Subscription Term</FormLabel>
                <RadioGroup
                  row
                  value={formState.subscriptionTerm}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      subscriptionTerm: Number(
                        e.target.value,
                      ) as SubscriptionTerm,
                    })
                  }
                >
                  <FormControlLabel
                    value={SubscriptionTerm.MONTHLY}
                    control={<Radio />}
                    label="Monthly"
                    disabled={isProcessing}
                  />
                  <FormControlLabel
                    value={SubscriptionTerm.ANNUAL}
                    control={<Radio />}
                    label="Annual"
                    disabled={isProcessing}
                  />
                </RadioGroup>
              </FormControl>
            )}

            {requiresCoupon && (
              <TextField
                fullWidth
                margin="dense"
                label="Coupon Code"
                variant="outlined"
                value={formState.couponCode}
                onChange={(e) =>
                  setFormState({ ...formState, couponCode: e.target.value })
                }
                disabled={isProcessing}
                required
                inputProps={{ "data-testid": "coupon-code-input" }}
              />
            )}

            {error && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
                data-testid="error-message"
              >
                {error.message}
              </Alert>
            )}
          </>
        )}
      </Box>
    </GenericDialog>
  );
};

export default CreateOrgDialog;
